import React, { useEffect, useRef, useState } from 'react';
import api, { cases as casesApi, errorMessage } from '../utils/api';
import { formatDateTime } from '../utils/format';
import { PRESCRIPTION_MAX_BYTES, PRESCRIPTION_TYPES } from '../utils/constants';
import Modal from './Modal';

/*
 * نسخه‌ی پزشک.
 *
 * فایل پشت ویوی دارای احراز هویت است، پس مثل یادداشت صوتی نمی‌شود مستقیم در
 * src گذاشت — با axios می‌گیریم و به object URL تبدیل می‌کنیم.
 */

function useAuthedFile(url) {
  const [objectUrl, setObjectUrl] = useState(null);
  const [type, setType] = useState('');
  const [error, setError] = useState('');
  const created = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!url) return undefined;

    api
      .get(url, { responseType: 'blob', baseURL: '' })
      .then(({ data }) => {
        if (cancelled) return;
        const objUrl = URL.createObjectURL(data);
        created.current = objUrl;
        setObjectUrl(objUrl);
        setType(data.type || '');
      })
      .catch(() => !cancelled && setError('بارگذاری تصویر ممکن نشد.'));

    return () => {
      cancelled = true;
      if (created.current) {
        URL.revokeObjectURL(created.current);
        created.current = null;
      }
    };
  }, [url]);

  return { objectUrl, type, error };
}

function Thumb({ item, onOpen }) {
  const { objectUrl, type, error } = useAuthedFile(item.url);
  const isPdf = (type || item.content_type || '').includes('pdf');

  return (
    <button type="button" className="rx-thumb" onClick={() => onOpen(item)} title={item.original_name}>
      {error ? (
        <span className="rx-thumb__fallback">؟</span>
      ) : isPdf ? (
        <span className="rx-thumb__fallback">PDF</span>
      ) : objectUrl ? (
        <img src={objectUrl} alt={item.original_name || 'نسخه‌ی پزشک'} />
      ) : (
        <span className="rx-thumb__fallback">…</span>
      )}
    </button>
  );
}

function Viewer({ item, onClose }) {
  const { objectUrl, type, error } = useAuthedFile(item?.url);
  if (!item) return null;
  const isPdf = (type || item.content_type || '').includes('pdf');

  return (
    <Modal open title="نسخه‌ی پزشک" onClose={onClose} width={860}>
      {error && <p className="alert alert--danger">{error}</p>}
      {!error && !objectUrl && <p className="muted">در حال بارگذاری…</p>}
      {objectUrl && isPdf && (
        <iframe src={objectUrl} title="نسخه‌ی پزشک" className="rx-pdf" />
      )}
      {objectUrl && !isPdf && (
        <img src={objectUrl} alt="نسخه‌ی پزشک" className="rx-full" />
      )}
      <p className="form__note">
        بارگذاری: <bdi>{item.uploaded_by_name}</bdi> · <time>{formatDateTime(item.created_at)}</time>
      </p>
    </Modal>
  );
}

/** نمایش نسخه‌ها — برای تصویربرداری، ادیتور و مسئول فنی. */
export function PrescriptionStrip({ items }) {
  const [open, setOpen] = useState(null);
  if (!items?.length) return null;

  return (
    <div className="rx">
      <span className="rx__label">📄 نسخه‌ی پزشک</span>
      <div className="rx__list">
        {items.map((item) => (
          <Thumb key={item.id} item={item} onOpen={setOpen} />
        ))}
      </div>
      <Viewer item={open} onClose={() => setOpen(null)} />
    </div>
  );
}

/**
 * جدا کردن فایل‌های قابل‌قبول از بقیه.
 *
 * سرور هم همین بررسی را می‌کند؛ این یکی فقط برای آن است که کاربر به‌جای
 * انتظار برای آپلود و دیدن خطای سرور، فوراً بفهمد چه چیزی رد شده است.
 */
function sift(files) {
  const accepted = [];
  const rejected = [];

  for (const file of files) {
    const type = (file.type || '').split(';')[0].trim().toLowerCase();
    if (!PRESCRIPTION_TYPES.includes(type)) {
      rejected.push(`«${file.name}» تصویر یا PDF نیست.`);
    } else if (file.size > PRESCRIPTION_MAX_BYTES) {
      rejected.push(`«${file.name}» بزرگ‌تر از ۱۵ مگابایت است.`);
    } else {
      accepted.push(file);
    }
  }

  return { accepted, rejected };
}

/** بارگذاری و مدیریت نسخه — فقط برای پذیرش. */
export function PrescriptionUploader({ caseId, items, onChanged, disabled }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const [dragging, setDragging] = useState(false);
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  // شمارنده لازم است چون dragleave روی هر فرزند هم شلیک می‌شود و بدون آن
  // کادر با عبور اشاره‌گر از روی یک تصویر کوچک، چشمک می‌زند.
  const depth = useRef(0);

  const send = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const { accepted, rejected } = sift(files);
    setError(rejected.join(' '));
    if (!accepted.length) return;

    setBusy(true);
    try {
      // یکی‌یکی، نه موازی: چند عکس گوشی هم‌زمان، اتصال کند را خفه می‌کند.
      for (let i = 0; i < accepted.length; i += 1) {
        setProgress(accepted.length > 1 ? `${i + 1} از ${accepted.length}` : '');
        await casesApi.addPrescription(caseId, accepted[i]);
      }
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err, 'بارگذاری انجام نشد.'));
      onChanged?.();
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  const pick = (event) => {
    /*
     * ترتیب این دو خط مهم است و جای اشتباه‌کردن دارد.
     *
     * event.target.files یک FileList زنده است، نه کپی. پاک کردن مقدار ورودی
     * همان فهرست را هم خالی می‌کند — پس اول باید به آرایه تبدیلش کرد و بعد
     * ورودی را پاک کرد. برعکسش یعنی آپلود بی‌صدا هیچ کاری نمی‌کند.
     *
     * پاک کردن ورودی لازم است، وگرنه انتخاب دوباره‌ی همان فایل رویداد
     * change نمی‌دهد.
     */
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    send(files);
  };

  const remove = async (item) => {
    setBusy(true);
    setError('');
    try {
      await casesApi.removePrescription(caseId, item.id);
      onChanged?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const blocked = busy || disabled;

  // --- کشیدن و رها کردن ---

  const onDragEnter = (event) => {
    if (blocked) return;
    event.preventDefault();
    depth.current += 1;
    setDragging(true);
  };

  const onDragOver = (event) => {
    if (blocked) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    depth.current = Math.max(depth.current - 1, 0);
    if (depth.current === 0) setDragging(false);
  };

  const onDrop = (event) => {
    event.preventDefault();
    depth.current = 0;
    setDragging(false);
    if (blocked) return;
    send(event.dataTransfer?.files);
  };

  return (
    <div
      className={`rx rx--drop ${dragging ? 'is-dragging' : ''}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <span className="rx__label">📄 نسخه‌ی پزشک</span>

      {items?.length > 0 && (
        <div className="rx__list">
          {items.map((item) => (
            <div key={item.id} className="rx__item">
              <Thumb item={item} onOpen={setOpen} />
              <button
                type="button"
                className="rx__remove"
                onClick={() => remove(item)}
                disabled={blocked}
                aria-label="حذف تصویر"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="rx__actions">
        {/* capture دوربین پشتی گوشی را باز می‌کند؛ روی دسکتاپ نادیده گرفته می‌شود */}
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={pick}
          hidden
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          onChange={pick}
          hidden
        />
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => cameraRef.current?.click()}
          disabled={blocked}
        >
          📷 عکس با دوربین
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => fileRef.current?.click()}
          disabled={blocked}
        >
          🖼️ انتخاب فایل
        </button>
        {busy ? (
          <span className="muted">در حال بارگذاری… {progress}</span>
        ) : (
          !disabled && <span className="rx__hint">یا فایل را اینجا رها کنید</span>
        )}
      </div>

      {dragging && !blocked && (
        <div className="rx__dropzone" aria-hidden="true">
          <span>📥 رها کنید تا بارگذاری شود</span>
        </div>
      )}

      {error && <p className="field__error">{error}</p>}
      <Viewer item={open} onClose={() => setOpen(null)} />
    </div>
  );
}
